import React from 'react';
import {
  Activity,
  AlertTriangle,
  Pause,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { HealthService, IntervalSeconds, PollSnapshot, ServiceProbeStatus } from './types';
import { INTERVAL_OPTIONS } from './types';
import { getServiceIcon, humanizeServiceId } from './serviceIcons';

export function latencyColor(ms: number, isLight: boolean): string {
  if (ms < 100) return isLight ? 'text-emerald-600' : 'text-emerald-400';
  if (ms < 200) return isLight ? 'text-amber-600' : 'text-amber-400';
  return isLight ? 'text-red-600' : 'text-red-400';
}

export function latencyBarColor(ms: number): string {
  if (ms < 100) return 'bg-emerald-500';
  if (ms < 200) return 'bg-amber-500';
  return 'bg-red-500';
}

export function statusPillClass(status: ServiceProbeStatus, isLight: boolean): string {
  const base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wide';
  if (status === 'up') {
    return `${base} ${isLight ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}`;
  }
  if (status === 'down') {
    return `${base} ${isLight ? 'bg-red-50 text-red-700 border-red-200' : 'bg-red-500/10 text-red-400 border-red-500/30'}`;
  }
  return `${base} ${isLight ? 'bg-slate-50 text-slate-600 border-slate-200' : 'bg-slate-500/10 text-slate-400 border-slate-500/30'}`;
}

export function overallBannerClass(status: string, isLight: boolean): string {
  if (status === 'ok') {
    return isLight
      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100';
  }
  if (status === 'degraded') {
    return isLight
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-amber-500/10 border-amber-500/30 text-amber-100';
  }
  return isLight
    ? 'bg-red-50 border-red-200 text-red-900'
    : 'bg-red-500/10 border-red-500/30 text-red-100';
}

export function formatRelative(isoOrMs: string | number): string {
  const t = typeof isoOrMs === 'number' ? isoOrMs : Date.parse(isoOrMs);
  if (Number.isNaN(t)) return '—';
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export function formatLatency(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  return `${ms.toFixed(ms >= 100 ? 0 : 1)} ms`;
}

/* ── Live controls ─────────────────────────────────────────────── */

export function LiveControls({
  live,
  intervalSec,
  countdown,
  loading,
  isLight,
  onToggleLive,
  onIntervalChange,
  onRefresh,
}: {
  live: boolean;
  intervalSec: IntervalSeconds;
  countdown: number;
  loading: boolean;
  isLight: boolean;
  onToggleLive: () => void;
  onIntervalChange: (sec: IntervalSeconds) => void;
  onRefresh: () => void;
}) {
  const muted = isLight ? 'text-slate-500' : 'text-slate-400';
  const btn = isLight
    ? 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
    : 'border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-slate-200';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onToggleLive}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
          live
            ? isLight
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
            : btn
        }`}
        aria-pressed={live}
      >
        {live ? (
          <Activity size={13} className="animate-pulse" />
        ) : (
          <Pause size={13} />
        )}
        {live ? 'Live' : 'Paused'}
      </button>

      <label className={`inline-flex items-center gap-1.5 text-xs ${muted}`}>
        <span className="sr-only">Refresh interval</span>
        <select
          value={intervalSec}
          disabled={!live}
          onChange={(e) => onIntervalChange(Number(e.target.value) as IntervalSeconds)}
          className={`rounded-lg border text-xs px-2 py-1.5 disabled:opacity-50 ${
            isLight
              ? 'border-slate-200 bg-white text-slate-700'
              : 'border-neutral-700 bg-neutral-900 text-slate-200'
          }`}
        >
          {INTERVAL_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}s
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-60 ${btn}`}
      >
        <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        Refresh
      </button>

      {live && (
        <span className={`font-mono text-[11px] tabular-nums ${muted}`}>
          Next in {countdown}s
        </span>
      )}
    </div>
  );
}

/* ── KPI ───────────────────────────────────────────────────────── */

export function KpiTile({
  label,
  value,
  icon: Icon,
  accent,
  isLight,
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;
  accent: 'emerald' | 'red' | 'amber' | 'indigo';
  isLight: boolean;
}) {
  const accents = {
    emerald: isLight ? 'text-emerald-600 bg-emerald-50' : 'text-emerald-400 bg-emerald-500/10',
    red: isLight ? 'text-red-600 bg-red-50' : 'text-red-400 bg-red-500/10',
    amber: isLight ? 'text-amber-600 bg-amber-50' : 'text-amber-400 bg-amber-500/10',
    indigo: isLight ? 'text-indigo-600 bg-indigo-50' : 'text-indigo-400 bg-indigo-500/10',
  } as const;

  return (
    <div
      className={`rounded-lg border p-3 ${
        isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-neutral-800'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className={`text-[11px] font-medium ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
          {label}
        </p>
        <span className={`inline-flex p-1 rounded-md ${accents[accent]}`}>
          <Icon size={13} />
        </span>
      </div>
      <p className={`text-2xl font-semibold tabular-nums tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`}>
        {value}
      </p>
    </div>
  );
}

/* ── Charts ────────────────────────────────────────────────────── */

function ChartCard({
  title,
  subtitle,
  isLight,
  children,
}: {
  title: string;
  subtitle?: string;
  isLight: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-neutral-800'
      }`}
    >
      <div className={`px-4 py-2.5 border-b ${isLight ? 'border-slate-100' : 'border-neutral-800'}`}>
        <h3 className={`text-xs font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
          {title}
        </h3>
        {subtitle && (
          <p className={`text-[10px] mt-0.5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
            {subtitle}
          </p>
        )}
      </div>
      <div className="px-2 py-3 h-44">{children}</div>
    </div>
  );
}

function Collecting({ isLight }: { isLight: boolean }) {
  return (
    <div className={`h-full flex items-center justify-center text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
      Collecting live samples…
    </div>
  );
}

export function HealthCharts({
  history,
  requiredUp,
  requiredDown,
  optionalDown,
  isLight,
}: {
  history: PollSnapshot[];
  requiredUp: number;
  requiredDown: number;
  optionalDown: number;
  isLight: boolean;
}) {
  const grid = isLight ? '#e2e8f0' : '#262626';
  const tick = isLight ? '#64748b' : '#a3a3a3';
  const tipStyle = {
    background: isLight ? '#fff' : '#171717',
    border: `1px solid ${isLight ? '#e2e8f0' : '#404040'}`,
    borderRadius: 8,
    fontSize: 11,
  };

  const series = history.map((h) => ({
    t: new Date(h.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    up: h.up,
    down: h.down,
    avgLatency: Math.round(h.avgLatency * 10) / 10,
  }));

  const donut = [
    { name: 'Required up', value: requiredUp, color: '#10b981' },
    { name: 'Required down', value: requiredDown, color: '#ef4444' },
    { name: 'Optional down', value: optionalDown, color: '#f59e0b' },
  ].filter((d) => d.value > 0);

  const ready = history.length >= 2;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <ChartCard title="Fleet availability" subtitle="Up vs down over live polls" isLight={isLight}>
        {!ready ? (
          <Collecting isLight={isLight} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="upFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="downFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="t" tick={{ fill: tick, fontSize: 9 }} tickLine={false} axisLine={false} minTickGap={28} />
              <YAxis allowDecimals={false} tick={{ fill: tick, fontSize: 9 }} tickLine={false} axisLine={false} width={28} />
              <Tooltip contentStyle={tipStyle} />
              <Area type="monotone" dataKey="up" name="Up" stroke="#10b981" fill="url(#upFill)" strokeWidth={1.5} isAnimationActive={false} />
              <Area type="monotone" dataKey="down" name="Down" stroke="#ef4444" fill="url(#downFill)" strokeWidth={1.5} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Avg latency" subtitle="Fleet average latency (ms)" isLight={isLight}>
        {!ready ? (
          <Collecting isLight={isLight} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="latFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="t" tick={{ fill: tick, fontSize: 9 }} tickLine={false} axisLine={false} minTickGap={28} />
              <YAxis tick={{ fill: tick, fontSize: 9 }} tickLine={false} axisLine={false} width={36} />
              <Tooltip contentStyle={tipStyle} />
              <Area
                type="monotone"
                dataKey="avgLatency"
                name="Avg ms"
                stroke="#6366f1"
                fill="url(#latFill)"
                strokeWidth={1.75}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Required vs optional" subtitle="Current probe breakdown" isLight={isLight}>
        {donut.length === 0 ? (
          <Collecting isLight={isLight} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donut}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={62}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {donut.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tipStyle} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

/* ── Sparkline ─────────────────────────────────────────────────── */

export function LatencySparkline({
  points,
  isLight,
}: {
  points: number[];
  isLight: boolean;
}) {
  if (points.length < 3) return null;
  const max = Math.max(...points, 1);
  const w = 72;
  const h = 22;
  const pts = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - 2 - (v / max) * (h - 4);
      return `${x},${y}`;
    })
    .join(' ');
  const stroke = isLight ? '#6366f1' : '#818cf8';

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block opacity-80" aria-hidden>
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ── Service card ──────────────────────────────────────────────── */

export function ServiceCard({
  service,
  spark,
  flipped,
  isLight,
  maxLatency,
}: {
  service: HealthService;
  spark: number[];
  flipped: boolean;
  isLight: boolean;
  maxLatency: number;
}) {
  const Icon = getServiceIcon(service.id);
  const barPct = Math.min(100, (service.latency_ms / Math.max(maxLatency, 1)) * 100);

  return (
    <div
      className={`rounded-lg border p-3 transition-colors duration-500 ${
        flipped
          ? isLight
            ? 'border-indigo-300 bg-indigo-50/60'
            : 'border-indigo-500/40 bg-indigo-500/10'
          : isLight
            ? 'bg-white border-slate-200'
            : 'bg-neutral-900 border-neutral-800'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={`mt-0.5 inline-flex p-1.5 rounded-md shrink-0 ${
            service.status === 'up'
              ? isLight
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-emerald-500/10 text-emerald-400'
              : isLight
                ? 'bg-red-50 text-red-600'
                : 'bg-red-500/10 text-red-400'
          }`}
        >
          <Icon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h4 className={`text-xs font-semibold truncate ${isLight ? 'text-slate-900' : 'text-slate-100'}`}>
              {humanizeServiceId(service.id)}
            </h4>
            <span className={statusPillClass(service.status, isLight)}>{service.status}</span>
            {service.required && (
              <span
                className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium border ${
                  isLight
                    ? 'bg-slate-50 text-slate-600 border-slate-200'
                    : 'bg-neutral-800 text-slate-300 border-neutral-700'
                }`}
              >
                Required
              </span>
            )}
          </div>
          <p
            className={`mt-1 text-[10px] font-mono truncate ${isLight ? 'text-slate-500' : 'text-slate-400'}`}
            title={service.url}
          >
            {service.url}
          </p>
        </div>
        <LatencySparkline points={spark} isLight={isLight} />
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className={`text-[10px] ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Latency</span>
            <span className={`text-xs font-mono tabular-nums font-medium ${latencyColor(service.latency_ms, isLight)}`}>
              {formatLatency(service.latency_ms)}
            </span>
          </div>
          <div className={`h-1 rounded-full overflow-hidden ${isLight ? 'bg-slate-100' : 'bg-neutral-800'}`}>
            <div className={`h-full rounded-full ${latencyBarColor(service.latency_ms)}`} style={{ width: `${barPct}%` }} />
          </div>
        </div>
        <div className="text-right shrink-0">
          {service.http_status != null ? (
            <span className={`text-[11px] font-mono tabular-nums ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
              HTTP {service.http_status}
            </span>
          ) : service.error ? (
            <span
              className={`text-[10px] max-w-[9rem] truncate block ${isLight ? 'text-red-600' : 'text-red-400'}`}
              title={service.error}
            >
              {service.error}
            </span>
          ) : (
            <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>—</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Error / empty ─────────────────────────────────────────────── */

export function ErrorBanner({
  message,
  isLight,
  onRetry,
}: {
  message: string;
  isLight: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ${
        isLight ? 'bg-red-50 border-red-200 text-red-700' : 'bg-red-500/10 border-red-500/30 text-red-300'
      }`}
    >
      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="font-medium">Unable to reach health monitor</p>
        <p className="mt-0.5 opacity-90 break-words">{message}</p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className={`shrink-0 px-2 py-1 rounded border text-[11px] font-medium ${
          isLight ? 'border-red-200 bg-white hover:bg-red-50' : 'border-red-500/40 hover:bg-red-500/20'
        }`}
      >
        Retry
      </button>
    </div>
  );
}

export function SkeletonGrid({ isLight }: { isLight: boolean }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={`rounded-lg border p-3 animate-pulse h-28 ${
            isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-neutral-800'
          }`}
        >
          <div className={`h-3 w-1/2 rounded ${isLight ? 'bg-slate-200' : 'bg-neutral-700'}`} />
          <div className={`h-2 w-3/4 rounded mt-3 ${isLight ? 'bg-slate-100' : 'bg-neutral-800'}`} />
          <div className={`h-1 w-full rounded mt-6 ${isLight ? 'bg-slate-100' : 'bg-neutral-800'}`} />
        </div>
      ))}
    </div>
  );
}
